/**
 * GTM dataLayer helpers — mirror the defensive pattern of mixpanel.util.ts.
 * The GTM container is only injected on production (app.component.ts), so on
 * dev/staging window.dataLayer is undefined: gtmPush self-initializes the array
 * and every entry point is a silent no-op on failure. Analytics must never
 * break user flows.
 */

const AD_PARAM_STORAGE_KEY = 'rw.ad_params';
/** The only acquisition keys we ever capture/register — the allowlist, single source of truth. */
export const AD_PARAM_NAMES = [
  'gclid', 'gbraid', 'wbraid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
] as const;

/**
 * Grab gclid/utm params from the entry URL and stash them in sessionStorage.
 *
 * A URL that carries ad params REPLACES the stored set wholesale — never merges, never
 * blank-fills. This mirrors landing-page/assets/js/ad-params.js, which does the same:
 * a `?gclid=B`-only click must not inherit `utm_source` from a previous campaign's
 * stored set and fabricate a hybrid attribution.
 *
 * Previously this returned early whenever a store already existed (write-once per tab),
 * which diverged from the landing: a second campaign click in the same tab was silently
 * ignored and last-touch went stale.
 *
 * A URL with NO ad params leaves any existing store untouched, so attribution survives
 * internal SPA navigation.
 *
 * `search` is resolved INSIDE the try, not as a default parameter value. Default params
 * evaluate before the function body, so `search = window.location.search` would throw
 * past this try/catch in any non-browser context. That was survivable while this ran
 * from a component; it is not now that main.ts calls it pre-bootstrap, where a throw
 * costs the whole app rather than one analytics prop.
 */
export function captureAdParams(search?: string): void {
  try {
    const query = typeof search === 'string'
      ? search
      : (typeof window !== 'undefined' ? window.location.search : '');
    const url = new URLSearchParams(query);
    const params: Record<string, string> = {};
    for (const name of AD_PARAM_NAMES) {
      const value = url.get(name);
      if (value) params[name] = value;
    }
    if (Object.keys(params).length > 0) {
      sessionStorage.setItem(AD_PARAM_STORAGE_KEY, JSON.stringify(params));
    }
  } catch {
    // no-op: analytics must not break user flows.
  }
}

/** Retrieve stored ad params — ALWAYS a plain object (empty on absence/failure/non-object). */
export function getStoredAdParams(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(AD_PARAM_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    // JSON.parse can legally yield null / arrays / primitives (e.g. raw === 'null') —
    // never return those, so callers can safely index the result without crashing.
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch {
    return {};
  }
}

/** SHA-256 hex digest via Web Crypto (async, secure contexts only). */
export async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Push a payload to window.dataLayer, creating the array if GTM has not loaded. */
export function gtmPush(payload: Record<string, unknown>): void {
  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = Array.isArray(w.dataLayer) ? w.dataLayer : [];
    w.dataLayer.push(payload);
  } catch {
    // no-op: analytics must not break user flows.
  }
}

/**
 * Fire-and-forget registration event. Hashing is async; dataLayer is a global
 * array that survives SPA navigation, so a push that lands moments after
 * router.navigate() still reaches GTM.
 */
export async function gtmTrackRegistrationComplete(
  user: { _id?: string; email?: string } | undefined,
  registrationMethod: string
): Promise<void> {
  try {
    if (!user?._id) return;
    const email = (user.email ?? '').trim().toLowerCase();
    const adParams = getStoredAdParams();
    gtmPush({
      event: 'registration_complete',
      user_id: user._id,
      registration_method: registrationMethod,
      // RAW-EMAIL OVERRIDE POINT — swap email_sha256 to { email } only with explicit client PII sign-off; update the GTM variable config downstream to match.
      email_sha256: email ? await sha256Hex(email) : undefined,
      ...adParams,
    });
  } catch {
    // no-op: analytics must not break user flows.
  }
}
